import { PortableText, type PortableTextComponents } from '@portabletext/react';
import type { Href } from 'expo-router';
import { Image } from 'expo-image';
import { StyleSheet, Text, View } from 'react-native';

import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { urlForImage } from '@todo/core/sanity/client';
import type { ArticleDetail } from '@todo/core/sanity/queries';

import { internalPath, ProseLink } from './prose-link';

/**
 * Maps authored Sanity heading styles onto a contiguous outline starting at 2.
 *
 * Returns a function from the authored level (the N in a `hN` block style) to
 * the level to render. The shallowest style actually used in the document
 * becomes 2, so a document written entirely in h4/h5 emits h2/h3, and one
 * written in h1/h2 also emits h2/h3. Clamped to the 2–6 range.
 */
function normaliseHeadingLevels(blocks: ArticleDetail['body']): (authored: number) => number {
  const used = new Set<number>();
  for (const block of blocks ?? []) {
    const style = (block as { _type?: string; style?: string }).style;
    if ((block as { _type?: string })._type !== 'block' || typeof style !== 'string') continue;
    const match = /^h([1-6])$/.exec(style);
    if (match) used.add(Number(match[1]));
  }
  const shallowest = used.size > 0 ? Math.min(...used) : 1;
  return (authored) => Math.min(6, Math.max(2, authored - shallowest + 2));
}

export function PortableTextBody({ value }: { value: ArticleDetail['body'] }) {
  const theme = useTheme();

  if (!value || value.length === 0) return null;

  // Sanity block styles rendered as real heading elements. Without an explicit
  // role, react-native-web renders <Text> as a plain <div>, so an article's
  // section headings carried no structure at all.
  //
  // The level is NORMALISED rather than copied. Authors have tagged sections
  // with whatever style looked right — the festivals article uses h4 and h5 for
  // what are plainly its top-level and second-level sections — so copying the
  // authored number would emit <h1> followed by <h5>, a skipped-level outline.
  // Instead the shallowest heading style present in *this* document becomes
  // <h2> (directly under the page's <h1>) and the rest keep their relative
  // depth from there. Re-tagging the content in Sanity is still worth doing;
  // this makes the output correct either way.
  const headingLevel = normaliseHeadingLevels(value);

  const components: PortableTextComponents = {
    block: {
      normal: ({ children }) => (
        <View style={styles.paragraphBlock}>
          <Text style={[styles.paragraph, { color: theme.text }]}>{children}</Text>
        </View>
      ),
      h1: ({ children }) => (
        <View style={styles.h1Block}>
          <Text
            role="heading"
            aria-level={headingLevel(1)}
            style={[styles.h1, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {children}
          </Text>
        </View>
      ),
      h2: ({ children }) => (
        <View style={styles.h2Block}>
          <Text
            role="heading"
            aria-level={headingLevel(2)}
            style={[styles.h2, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {children}
          </Text>
        </View>
      ),
      h3: ({ children }) => (
        <View style={styles.h3Block}>
          <Text
            role="heading"
            aria-level={headingLevel(3)}
            style={[styles.h3, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {children}
          </Text>
        </View>
      ),
      h4: ({ children }) => (
        <View style={styles.h4Block}>
          <Text
            role="heading"
            aria-level={headingLevel(4)}
            style={[styles.h4, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {children}
          </Text>
        </View>
      ),
      h5: ({ children }) => (
        <View style={styles.h4Block}>
          <Text
            role="heading"
            aria-level={headingLevel(5)}
            style={[styles.h4, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {children}
          </Text>
        </View>
      ),
      h6: ({ children }) => (
        <View style={styles.h4Block}>
          <Text
            role="heading"
            aria-level={headingLevel(6)}
            style={[styles.h4, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {children}
          </Text>
        </View>
      ),
      blockquote: ({ children }) => (
        <View style={[styles.blockquote, { borderColor: theme.gold }]}>
          <Text style={[styles.blockquoteText, { color: theme.mutedForeground }]}>
            {children}
          </Text>
        </View>
      ),
    },
    list: {
      bullet: ({ children }) => <View style={styles.list}>{children}</View>,
      number: ({ children }) => <View style={styles.list}>{children}</View>,
    },
    listItem: {
      bullet: ({ children }) => (
        <View style={styles.listItem}>
          <Text style={[styles.listMarker, { color: theme.text }]}>{'•'}</Text>
          <Text style={[styles.paragraph, styles.listItemText, { color: theme.text }]}>
            {children}
          </Text>
        </View>
      ),
      number: ({ children, index }) => (
        <View style={styles.listItem}>
          <Text style={[styles.listMarker, { color: theme.text }]}>{index + 1}.</Text>
          <Text style={[styles.paragraph, styles.listItemText, { color: theme.text }]}>
            {children}
          </Text>
        </View>
      ),
    },
    marks: {
      strong: ({ children }) => <Text style={styles.strong}>{children}</Text>,
      em: ({ children }) => <Text style={styles.em}>{children}</Text>,
      code: ({ children }) => (
        <Text style={[styles.code, { fontFamily: Fonts?.mono, backgroundColor: theme.muted }]}>
          {children}
        </Text>
      ),
      underline: ({ children }) => <Text style={styles.underline}>{children}</Text>,
      'strike-through': ({ children }) => <Text style={styles.strikeThrough}>{children}</Text>,
      // Without this renderer @portabletext/react emits a bare <a>, i.e. the
      // browser's default blue. Authors paste absolute riviera-todo.com URLs,
      // which internalPath() turns back into client-side router links.
      link: ({ children, value: mark }) => {
        const href = typeof mark?.href === 'string' ? mark.href.trim() : '';
        if (!href) return <>{children}</>;
        const path = internalPath(href);
        return path ? (
          <ProseLink href={path as Href}>{children}</ProseLink>
        ) : (
          <ProseLink href={href as Href} external>
            {children}
          </ProseLink>
        );
      },
    },
    types: {
      image: ({ value: imageValue }) => {
        const uri = urlForImage(imageValue).width(800).url();
        return (
          <View style={styles.imageBlock}>
            <Image
              source={{ uri }}
              style={styles.image}
              contentFit="cover"
              accessibilityLabel={imageValue.alt ?? imageValue.caption ?? undefined}
            />
            {imageValue.caption ? (
              <Text style={[styles.caption, { color: theme.mutedForeground }]}>
                {imageValue.caption}
              </Text>
            ) : null}
          </View>
        );
      },
    },
  };

  return <PortableText value={value} components={components} />;
}

const styles = StyleSheet.create({
  paragraph: {
    fontSize: 16,
    lineHeight: 26,
  },
  paragraphBlock: {
    marginBottom: Spacing.three,
  },
  h1: {
    fontSize: 28,
    fontWeight: '700',
  },
  h1Block: {
    marginTop: Spacing.three,
    marginBottom: Spacing.two,
  },
  h2: {
    fontSize: 24,
    fontWeight: '700',
  },
  h2Block: {
    marginTop: Spacing.three,
    marginBottom: Spacing.two,
  },
  h3: {
    fontSize: 20,
    fontWeight: '700',
  },
  h3Block: {
    marginTop: Spacing.two,
    marginBottom: Spacing.two,
  },
  h4: {
    fontSize: 17,
    fontWeight: '700',
  },
  h4Block: {
    marginTop: Spacing.two,
    marginBottom: Spacing.one,
  },
  blockquote: {
    borderLeftWidth: 3,
    paddingLeft: Spacing.three,
    marginBottom: Spacing.three,
  },
  blockquoteText: {
    fontSize: 16,
    lineHeight: 26,
    fontStyle: 'italic',
  },
  list: {
    marginBottom: Spacing.three,
    gap: Spacing.one,
  },
  listItem: {
    flexDirection: 'row',
    gap: Spacing.two,
  },
  listMarker: {
    fontSize: 16,
    lineHeight: 26,
  },
  listItemText: {
    flex: 1,
    marginBottom: 0,
  },
  strong: {
    fontWeight: '700',
  },
  em: {
    fontStyle: 'italic',
  },
  code: {
    fontSize: 14,
    paddingHorizontal: 4,
    borderRadius: Radius.sm,
  },
  underline: {
    textDecorationLine: 'underline',
  },
  strikeThrough: {
    textDecorationLine: 'line-through',
  },
  imageBlock: {
    marginBottom: Spacing.three,
    gap: Spacing.one,
  },
  image: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: Radius.lg,
  },
  caption: {
    fontSize: 13,
    textAlign: 'center',
  },
});
